import React from "react";
import { useTranslation } from "react-i18next";
import GrossSalaryRangeFilter from "./GrossSalaryRangeFilter";

/**
 * Filter shell aligned with Timesheet Approvals (ts-appr-filters), without period/view toggles.
 */
const SalaryAssignmentFilters = ({
  employeeQuery,
  onEmployeeQueryChange,
  departmentFilter,
  onDepartmentFilterChange,
  officeFilter,
  onOfficeFilterChange,
  projectFilter,
  onProjectFilterChange,
  payrollStructureFilter,
  onPayrollStructureFilterChange,
  grossFilter,
  onGrossFilterChange,
  grossBounds,
  departments = [],
  offices = [],
  projects = [],
  payrollStructures = [],
  exportLoading,
  onExportExcel,
  onExportCsv,
  onExportPdf,
  onClearFilters,
  listLoading = false,
}) => {
  const { t } = useTranslation();

  return (
    <section className="ts-time-log-shell" aria-label={t("app.payroll.assignments.filtersAria")}>
      <div className="ts-appr-filters payroll-assign-filters" role="search">
        <div className="ts-appr-filters__title text-muted" id="payroll-assign-filters-heading">
          {t("app.payroll.assignments.filtersToolbarLabel")}
        </div>

        <div className="ts-appr-filters__toolbar payroll-assign-filters__toolbar" aria-labelledby="payroll-assign-filters-heading">
          <GrossSalaryRangeFilter
            bounds={grossBounds}
            value={grossFilter}
            onChange={onGrossFilterChange}
            idPrefix="payroll-assign-gross"
          />
          <span className="ts-appr-filters__toolbar-divider payroll-assign-filters__toolbar-divider" aria-hidden />
          <div className="ts-appr-filters__toolbar-left payroll-assign-filters__toolbar-exports">
            <div
              className="ts-time-log-export-group ts-time-log-export-group--labeled ts-appr-filters__exports"
              role="group"
              aria-label={t("app.payroll.assignments.exportActionsAria")}
            >
              <button
                type="button"
                className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                onClick={onExportExcel}
                disabled={Boolean(exportLoading) || listLoading}
                title={t("app.payroll.assignments.exportExcelTitle")}
              >
                {exportLoading === "excel" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                )}
                <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
              </button>
              <button
                type="button"
                className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                onClick={onExportCsv}
                disabled={Boolean(exportLoading) || listLoading}
                title={t("app.payroll.assignments.exportCsvTitle")}
              >
                {exportLoading === "csv" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-filetype-csv" aria-hidden />
                )}
                <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
              </button>
              <button
                type="button"
                className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                onClick={onExportPdf}
                disabled={Boolean(exportLoading) || listLoading}
                title={t("app.payroll.assignments.exportPdfTitle")}
              >
                {exportLoading === "pdf" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-file-earmark-pdf" aria-hidden />
                )}
                <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="ts-appr-filters__fields payroll-assign-filters__fields">
          <div className="ts-filter-field">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-search">
              {t("app.payroll.assignments.searchEmployee")}
            </label>
            <input
              id="payroll-assign-search"
              type="search"
              autoComplete="off"
              placeholder={t("app.payroll.assignments.searchPlaceholder")}
              value={employeeQuery}
              onChange={(e) => onEmployeeQueryChange(e.target.value)}
            />
          </div>
          <div className="ts-filter-field">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-dept">
              {t("app.payroll.assignments.filterDepartment")}
            </label>
            <select
              id="payroll-assign-dept"
              value={departmentFilter}
              onChange={(e) => onDepartmentFilterChange(e.target.value)}
            >
              <option value="all">{t("app.payroll.assignments.filterAllDepartments")}</option>
              {departments.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ts-filter-field">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-office">
              {t("app.payroll.assignments.filterOffice")}
            </label>
            <select
              id="payroll-assign-office"
              value={officeFilter}
              onChange={(e) => onOfficeFilterChange(e.target.value)}
            >
              <option value="all">{t("app.payroll.assignments.filterAllOffices")}</option>
              {offices.map((o) => (
                <option key={o.id} value={String(o.id)}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ts-filter-field">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-project">
              {t("app.payroll.assignments.filterProject")}
            </label>
            <select
              id="payroll-assign-project"
              value={projectFilter}
              onChange={(e) => onProjectFilterChange(e.target.value)}
            >
              <option value="all">{t("app.payroll.assignments.filterAllProjects")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.teamName ? `${p.name} — ${p.teamName}` : p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ts-filter-field ts-appr-filters__filter--status">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-structure">
              {t("app.payroll.assignments.filterPayrollStructure")}
            </label>
            <select
              id="payroll-assign-structure"
              value={payrollStructureFilter}
              onChange={(e) => onPayrollStructureFilterChange(e.target.value)}
            >
              <option value="all">{t("app.payroll.assignments.filterAllStructures")}</option>
              <option value="none">{t("app.payroll.assignments.filterNoStructure")}</option>
              {payrollStructures.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ts-filter-field ts-appr-filters__clear-field">
            <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-assign-clear">
              {t("app.payroll.assignments.clearFiltersLabel")}
            </label>
            <button
              id="payroll-assign-clear"
              type="button"
              className="btn btn-outline-secondary ts-tl-clear-filters-btn ts-appr-filters__clear-btn"
              onClick={onClearFilters}
            >
              <i className="bi bi-x-lg me-1" aria-hidden />
              {t("app.payroll.assignments.clearFilters")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SalaryAssignmentFilters;
